import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { apiService } from "@services/ApiService";
import { ServerFile } from "@shared/interfaces/ProjectsFiles";
import { useNavigate, useParams } from "react-router-dom";
import { useProjectData } from "./useProjectData";
import { useProjectChat } from "@services/ProjectChatContext";
import { localFolderService } from "@services/LocalFolderService";
import { LocalFile } from "../components/Tabs/components/Files/LocalFilesFolders";

export const useProjectDocumentsTab = (
  activeTab: string,
  setRefreshOpenedFolder: React.Dispatch<React.SetStateAction<number>>,
  setRefreshGDriveExtFiles: React.Dispatch<React.SetStateAction<number>>,
  setRefreshGdriveFolders: React.Dispatch<React.SetStateAction<number>>,
  folderViewfiles: LocalFile[]
) => {
  const { canViewProjectFiles } = useProjectData();
  const { projectId, sharedToken } = useParams();
  const navigate = useNavigate();
  const dragCounter = useRef(0);

  const [files, setFiles] = useState<ServerFile[]>([]);
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const [isFileSelected, setIsFileSelected] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);

  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictedFiles, setConflictedFiles] = useState<File[]>([]);
  const [conflictedMeta, setConflictedMeta] = useState<
    Record<string, { folderId?: number }>
  >({});
  const [conflictActions, setConflictActions] = useState<
    Record<string, "overwrite" | "copy" | "cancel">
  >({});

  const FILES_PER_PAGE = 10;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fetchingFolderFiles, setFetchingFolderFiles] = useState(false);

  const { setFiles: setChatFiles } = useProjectChat();

  const fetchFiles = async (
    offset = 0,
    limit = FILES_PER_PAGE,
    scopeOrFolderIds: "all" | string[] = "all"
  ) => {
    if (!projectId) return;

    const isPublicShared = !!sharedToken;

    try {
      setFilesLoading(true);

      return await apiService.getFiles(projectId, sharedToken, isPublicShared, {
        offset,
        limit,
        scopeOrFolderIds,
      });

      return files;
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleChooseTemplate = async () => {
    try {
      if (!isFileSelected) {
        toast.error("Select a file(s) to generate a report", {
          autoClose: 2000,
        });
        return;
      }

      setFetchingFolderFiles(true);

      const reportName = `Report_${new Date().toISOString()}`;

      let allCheckedItems = [] as any[];

      const checkedFoldersIds = checkedItems.filter((id) =>
        id.endsWith("-folder")
      );
      if (checkedFoldersIds.length > 0) {
        const folderFiles = await fetchFiles(
          0,
          100,
          checkedFoldersIds.map((id) => id.replace("-folder", ""))
        );

        allCheckedItems = [...folderFiles?.files.map((f: any) => f.id)];
      }

      const checkedItemsWithoutSuffix = checkedItems
        .filter((id) => id.endsWith("-file") && !id.endsWith("-folder"))
        .map((id) =>
          id
            .replace("-gdrive-folder-file", "")
            .replace("-gdrive-ext-file", "")
            .replace("-local-file", "")
        );

      allCheckedItems.push(...checkedItemsWithoutSuffix);
      setFetchingFolderFiles(false);

      navigate(`/choose-template`, {
        state: {
          reportName,
          projectId,
          checkedItems: allCheckedItems,
        },
      });
    } catch (error) {
      console.error("Report creation error:", error);
    } finally {
      setFetchingFolderFiles(false);
    }
  };

  const uploadCall = async (
    projectId: string,
    file: File,
    overwrite = false,
    folderId?: number
  ) => {
    const uploadRes = await apiService.uploadFile(
      projectId,
      file,
      overwrite,
      folderId
    );
    setRefreshOpenedFolder((prev) => prev + 1);

    const fileId = uploadRes.data?.id;
    const taskId = uploadRes.data?.anomaly_task_id;

    if (
      taskId !== "" &&
      fileId &&
      taskId !== null &&
      fileId !== null &&
      taskId !== "null"
    ) {
      const task = {
        taskId,
        fileId,
        fileName: file.name,
        projectId,
        startedAt: Date.now(),
      };

      try {
        const existing = JSON.parse(
          localStorage.getItem("activeUploadTasks") || "[]"
        );
        localStorage.setItem(
          "activeUploadTasks",
          JSON.stringify([...existing, task])
        );
      } catch (error) {
        console.error("Error saving task to localStorage:", error);
      }
    } else {
      console.error("No taskId or fileId returned from upload response");
    }

    return uploadRes;
  };

  const handleFileUpload = async (filesToUpload: File[], folderId?: number) => {
    if (!projectId) return;
    const conflicted: File[] = [];
    const meta: Record<string, { folderId?: number }> = {};

    for (const originalFile of filesToUpload) {
      const normalizedFileName = originalFile.name.normalize("NFC");

      const file = new File([originalFile], normalizedFileName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
      });

      try {
        const res = await apiService.precheckFile(
          projectId,
          normalizedFileName,
          folderId
        );

        if (res.exists) {
          conflicted.push(file);
          meta[file.name] = { folderId };
        } else {
          setUploadingFile(file);
          await uploadCall(projectId, file, false, folderId);
        }
      } catch (error: any) {
        console.error("Error uploading file:", error);
      } finally {
        setUploadingFile(null);
      }
    }

    if (conflicted.length > 0) {
      setConflictedFiles(conflicted);
      setConflictedMeta(meta);
      setShowConflictModal(true);
    }
  };

  // NEW - handles single file uploads immediately (bypasses state lag)
  const uploadSingleFile = async (
    file: File,
    action: "overwrite" | "copy" | "cancel",
    folderIdOverride?: number
  ) => {
    if (action === "cancel" || !projectId) return;
    try {
      setUploadingFile(file);
      const fid = folderIdOverride ?? conflictedMeta[file.name]?.folderId;
      await uploadCall(projectId, file, action === "overwrite", fid);
    } catch (error: any) {
      console.error("Error uploading file:", error);
    } finally {
      setUploadingFile(null);
      // No state reset here — ConflictModal handles its own tracking now
    }
  };

  const handleConflictAction = (
    fileName: string,
    action: "overwrite" | "copy" | "cancel"
  ) => {
    setConflictActions((prev) => ({ ...prev, [fileName]: action }));
  };

  const handleDeleteSelectedItems = async () => {
    console.log("Deleting Items:", checkedItems);

    const folderIds = checkedItems
      .filter((id) => id.endsWith("-folder"))
      .map((id) => parseInt(id.replace("-folder", ""), 10));

    const gdriveFolderFiles = checkedItems.filter((id) =>
      id.endsWith("-gdrive-folder-file")
    ).length;
    const gdriveExtFiles = checkedItems.filter((id) =>
      id.endsWith("-gdrive-ext-file")
    ).length;

    const fileIds = checkedItems
      .filter((id) => id.endsWith("-file"))
      .map((id) =>
        id
          .replace("-gdrive-folder-file", "")
          .replace("-gdrive-ext-file", "")
          .replace("-local-file", "")
      );

    try {
      if (fileIds.length > 0) {
        await handleDeleteFile(fileIds);
      }

      if (folderIds.length > 0 && projectId) {
        await localFolderService.remove(folderIds, projectId);
      }

      if (gdriveFolderFiles > 0) {
        setRefreshGdriveFolders((prev) => prev + 1);
      }

      if (gdriveExtFiles > 0) {
        setRefreshGDriveExtFiles((prev) => prev + 1);
      }

      if (gdriveFolderFiles + gdriveExtFiles !== checkedItems.length) {
        setRefreshOpenedFolder((prev) => prev + 1);
      }

      setCheckedItems([]);
    } catch (error) {
      console.error("Error deleting items:", error);
    }
  };

  const handleProcessConflicts = async () => {
    if (!projectId) return;
    for (const file of conflictedFiles) {
      const action = conflictActions[file.name];

      if (action === "cancel") continue;

      try {
        setUploadingFile(file);
        const fid = conflictedMeta[file.name]?.folderId;
        await uploadCall(projectId, file, action === "overwrite", fid);
      } catch (error: any) {
        console.error("Error uploading file:", error);
      } finally {
        setUploadingFile(null);
      }
    }

    setShowConflictModal(false);
    setConflictedFiles([]);
    setConflictActions({});
    setConflictedMeta({});
  };

  const handleDeleteFile = async (fileIds: string[]) => {
    try {
      setFilesLoading(true);
      await apiService.deleteFile(projectId, fileIds);
    } catch (error: any) {
      console.error("Error deleting files:", error);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleDownloadSelectedItems = async () => {
    const checkedItemsWithoutSuffix = checkedItems
      .filter((id) => id.endsWith("-file"))
      .map((id) =>
        id
          .replace("-gdrive-folder-file", "")
          .replace("-gdrive-ext-file", "")
          .replace("-local-file", "")
      );

    for (const fileId of checkedItemsWithoutSuffix) {
      const file = folderViewfiles.find((f) => f.id === fileId);
      if (file) {
        try {
          await apiService.downloadFile(
            projectId,
            file.id,
            file.filename,
            file.filetype,
            sharedToken
          );
        } catch (error: any) {
          console.error(`Error downloading file ${file.filename}:`, error);
        }
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current += 1;
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setDragging(false);
    }
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    folderId: number | undefined | null
  ) => {
    if (activeTab.toLowerCase() !== "documents") return;

    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const acceptedFiles = droppedFiles.filter((file) =>
      [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
        "application/zip",
        "application/x-zip-compressed",
        "application/x-zip",
        "application/pdf",
      ].includes(file.type)
    );

    if (acceptedFiles.length > 0) {
      handleFileUpload(acceptedFiles, folderId ?? undefined);
    }
  };

  const clearConflictActions = () => {
    setConflictActions({});
  };

  useEffect(() => {
    setIsFileSelected(checkedItems.length > 0);
  }, [checkedItems]);

  useEffect(() => {
    setCheckedItems([]);
    setChatFiles([]);
    setConflictedMeta({});
    setChatFiles([]);
  }, [projectId]);

  return {
    files,
    setFiles,
    checkedItems,
    setCheckedItems,
    isFileSelected,
    uploadingFile,
    dragging,
    fetchFiles,
    handleChooseTemplate,
    handleFileUpload,
    handleDeleteSelectedItems,
    handleDeleteFile,
    handleDownloadSelectedItems,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    showConflictModal,
    conflictedFiles,
    clearConflictActions,
    conflictActions,
    handleConflictAction,
    handleProcessConflicts,
    uploadSingleFile,
    setShowConflictModal,
    filesLoading,
    setFilesLoading,
    currentPage,
    setCurrentPage,
    totalPages,
    fetchingFolderFiles,
  };
};
